import { useRef, useState } from 'react'

import { Input } from '@/components/ui/input'
import { Text } from '@/components/ui/text'
import { useOnClickOutside } from '@/hooks'
import { CommonCaretDown } from '@/icons'
import { WalletAction } from '@/types'

type Props = {
  options: WalletAction[] | null
  derivationPath: WalletAction | null
  setDerivationPath: (v: WalletAction) => void
  path: string
  setPath: (v: string) => void
  derivationIndex: string
  setDerivationIndex: (v: string) => void
}

export default function PathSelector({
  derivationPath,
  setDerivationPath,
  path,
  setPath,
  derivationIndex,
  setDerivationIndex,
  options,
}: Props) {
  const [showDerivationOptions, setShowDerivationOptions] = useState(false)

  const derivationOptionsRef = useRef<HTMLDivElement>(null)

  const updatePath = (index: string, action: WalletAction | null = derivationPath) => {
    if (action && !action.isCustom) {
      setPath(action.value.replace('{index}', index || '0'))
    }
  }

  const handleDerivationSelect = (action: WalletAction) => {
    setDerivationPath(action)
    setShowDerivationOptions(false)
    if (!action?.isCustom) setPath(action.value.replace('{index}', derivationIndex || '0'))
  }

  const handleIndexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value.replace(/\D/g, '')

    if (newValue !== '') {
      newValue = parseInt(newValue, 10).toString()
    }

    if (newValue.length > 1 && newValue[0] === '0') {
      newValue = newValue.slice(1)
    }

    if (newValue === '') {
      newValue = '0'
    }

    setDerivationIndex(newValue)
    updatePath(newValue)
  }

  useOnClickOutside(derivationOptionsRef, () => {
    setShowDerivationOptions(false)
  })

  if (!options) return null
  return (
    <div className='flex flex-col text-body-xs gap-2 mt-auto'>
      <div className='my-auto'>{derivationPath?.isCustom ? 'Path' : 'Index'}</div>
      <div className={'flex gap-1 ' + (derivationPath?.isCustom && 'flex-col-reverse')}>
        {derivationPath?.isCustom ? (
          <div className='w-full'>
            <Input
              className='my-auto text-size-12 h-8 bg-black-12 rounded-sm text-center  p-2'
              value={path}
              onChange={(e) => setPath(e.target.value)}
            />
          </div>
        ) : (
          <div className='w-[50px]'>
            <Input
              className='my-auto text-size-12 h-8 bg-black-12 rounded-sm text-center  p-2'
              value={derivationIndex}
              onChange={handleIndexChange}
              type='text'
              inputMode='numeric'
              pattern='\d*'
            />
          </div>
        )}
        <div className='relative flex-1'>
          {showDerivationOptions && (
            <div
              ref={derivationOptionsRef}
              className='absolute bottom-9 w-full rounded-sm border border-dark-4 bg-dark-background-24'
            >
              {options?.map((action, index) => (
                <Text
                  size={12}
                  key={index}
                  className={`cursor-pointer p-2 text-body-xs duration-300 hover:bg-white-12 ${
                    index === 0 ? 'rounded-t-sm' : index === (options?.length || 0) - 1 ? 'rounded-b-sm' : ''
                  }`}
                  onClick={() => handleDerivationSelect(action)}
                >
                  {action.nameOption}
                </Text>
              ))}
            </div>
          )}
          <div
            className='flex h-8 cursor-pointer items-center justify-between rounded-sm  ring-[1px] md:ring-[0.5px] [@media(max-resolution:1.9dppx)]:ring-[1px] ring-white-10 p-2'
            onClick={() => setShowDerivationOptions(!showDerivationOptions)}
          >
            <Text size={12} className='mr-1'>
              {derivationPath
                ? derivationPath.nameOption.replace('{index}', derivationIndex || '0')
                : options?.[0]?.nameOption.replace('{index}', derivationIndex || '0')}
            </Text>
            <CommonCaretDown className='text-white-48 size-2' />
          </div>
        </div>
      </div>
    </div>
  )
}
